import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, eur } from "@/lib/api";
import { AdminProducts } from "@/components/admin/AdminProducts";
import { AdminOrders } from "@/components/admin/AdminOrders";
import { AdminEvents, AdminCredit, AdminNewsletter, AdminB2B } from "@/components/admin/AdminExtras";
import { AdminInvoices } from "@/components/shop/MyData";
import { AdminContent } from "@/components/admin/AdminContent";
import { AdminPreorders } from "@/components/admin/AdminPreorders";
import { AdminTranslations } from "@/components/admin/AdminTranslations";
import { AdminInventory } from "@/components/admin/AdminInventory";
import { BellRing } from "lucide-react";
import { AdminRevenue } from "@/components/admin/AdminRevenue";
import { AdminCustomers } from "@/components/admin/AdminCustomers";
import { AdminShippingMargin } from "@/components/admin/AdminShippingMargin";
import { TestDataCleanup } from "@/components/admin/TestDataCleanup";
import { EmailTest } from "@/components/admin/EmailTest";
import { LastSync } from "@/components/admin/SyncSummary";
import { AdminStaff } from "@/components/admin/AdminStaff";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ label, value, accent, testid, onClick, active }) => (
  <button type="button" onClick={onClick} data-testid={`${testid}-card`}
    className={`rounded-2xl border-2 bg-[#FFFFFF] p-5 text-left transition-[transform,border-color,box-shadow] hover:-translate-y-0.5 hover:border-black hover:shadow-[4px_4px_0_#000] ${active ? "border-black shadow-[4px_4px_0_#000]" : "border-black/10"}`}>
    <p className="text-xs uppercase tracking-wider text-stone-500">{label}</p>
    <p className={`mt-2 font-mono text-2xl font-bold ${accent}`} data-testid={testid}>{value}</p>
  </button>
);

const STATS = [
  ["Incasso del mese", "incasso", "revenue", (s) => eur(s.revenue), () => "text-amber-600"],
  ["Ordini", "ordini", "orders", (s) => s.orders, () => ""],
  ["Da evadere", "evadere", "to-ship", (s) => s.to_ship, () => "text-cyan-600"],
  ["Prodotti", "prodotti", "products", (s) => s.products, () => ""],
  ["Clienti", "clienti", "customers", (s) => s.customers, () => "text-violet-500"],
  ["Pubblicati senza foto", "prodotti", "no-photo", (s) => s.no_photo, (s) => (s.no_photo ? "text-rose-500" : "text-emerald-600"), "photo_published"],
  ["Margine spedizioni", "margine", "shipping-margin", (s) => eur(s.shipping_margin), (s) => (s.shipping_margin < 0 ? "text-rose-500" : "text-emerald-600")],
];
const TO_SHIP = [["pagato,in_lavorazione", "Tutti da evadere"], ["pagato", "Pagati"], ["in_lavorazione", "In lavorazione"]];

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [prodFilter, setProdFilter] = useState("");
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "ordini");
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data));
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-12 sm:px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-violet-500">Area riservata</p>
          <h1 className="mt-2 font-display text-4xl font-bold" data-testid="admin-title">Pannello admin</h1>
        </div>
      </div>
      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8">
          {STATS.map(([label, t, id, val, acc, filter = ""]) => (
            <Stat key={id} label={label} value={val(stats)} accent={acc(stats)} testid={`stat-${id}`} active={tab === t && (t !== "prodotti" || prodFilter === filter)}
              onClick={() => { setTab(t); setProdFilter(filter); setTimeout(() => document.getElementById("admin-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }} />
          ))}
        </div>
      )}
      {stats?.preorders_due > 0 && (
        <button onClick={() => setTab("preordini")} className="mt-6 flex w-full items-center gap-3 rounded-2xl border-2 border-black bg-violet-500 p-4 text-left text-white shadow-[4px_4px_0_#000]" data-testid="preorder-alert">
          <BellRing className="h-6 w-6 shrink-0" />
          <span><b>{stats.preorders_due === 1 ? "1 ordine in preordine" : `${stats.preorders_due} ordini in preordine`}</b> {stats.preorders_due === 1 ? "ha" : "hanno"} raggiunto la data di uscita: avvia le spedizioni o richiedi i saldi.</span>
        </button>
      )}
      <AdminTranslations />
      <LastSync onOpen={() => { setTab("inventario"); setTimeout(() => document.getElementById("admin-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }} />
      <Tabs value={tab} onValueChange={setTab} className="mt-10 scroll-mt-32" id="admin-tabs">
        <TabsList className="h-auto flex-wrap justify-start bg-[#FFFFFF]">
          <TabsTrigger value="incasso" data-testid="admin-tab-revenue">Incasso</TabsTrigger>
          <TabsTrigger value="ordini" data-testid="admin-tab-orders">Ordini</TabsTrigger>
          <TabsTrigger value="evadere" data-testid="admin-tab-to-ship">Da evadere</TabsTrigger>
          <TabsTrigger value="preordini" data-testid="admin-tab-preorders">Preordini</TabsTrigger>
          <TabsTrigger value="prodotti" data-testid="admin-tab-products">Prodotti</TabsTrigger>
          <TabsTrigger value="clienti" data-testid="admin-tab-customers">Clienti</TabsTrigger>
          <TabsTrigger value="margine" data-testid="admin-tab-shipping">Spedizioni e margine</TabsTrigger>
          <TabsTrigger value="inventario" data-testid="admin-tab-inventory">Inventario</TabsTrigger>
          <TabsTrigger value="buylist" data-testid="admin-tab-credit">Credito clienti</TabsTrigger>
          <TabsTrigger value="eventi" data-testid="admin-tab-events">Eventi</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter">Email e newsletter</TabsTrigger>
          <TabsTrigger value="contenuti" data-testid="admin-tab-content">Contenuti</TabsTrigger>
          <TabsTrigger value="fatture" data-testid="admin-tab-invoices">Fatture</TabsTrigger>
          <TabsTrigger value="b2b" data-testid="admin-tab-b2b">B2B</TabsTrigger>
          {user?.is_owner && <TabsTrigger value="staff" data-testid="admin-tab-staff">Staff</TabsTrigger>}
        </TabsList>
        <TabsContent value="incasso" className="mt-6"><AdminRevenue onChange={loadStats} /></TabsContent>
        <TabsContent value="ordini" className="mt-6"><TestDataCleanup onDone={loadStats} /><AdminOrders onChange={loadStats} /></TabsContent>
        <TabsContent value="evadere" className="mt-6"><AdminOrders presets={TO_SHIP} onChange={loadStats} /></TabsContent>
        <TabsContent value="clienti" className="mt-6"><AdminCustomers onChange={loadStats} /></TabsContent>
        <TabsContent value="margine" className="mt-6"><AdminShippingMargin onChange={loadStats} /></TabsContent>
        <TabsContent value="preordini" className="mt-6"><AdminPreorders onChange={loadStats} /></TabsContent>
        <TabsContent value="prodotti" className="mt-6"><AdminProducts key={prodFilter} initialMissing={prodFilter} onChange={loadStats} /></TabsContent>
        <TabsContent value="inventario" className="mt-6"><AdminInventory /></TabsContent>
        <TabsContent value="buylist" className="mt-6"><AdminCredit /></TabsContent>
        <TabsContent value="eventi" className="mt-6"><AdminEvents /></TabsContent>
        <TabsContent value="newsletter" className="mt-6"><EmailTest /><AdminNewsletter /></TabsContent>
        <TabsContent value="contenuti" className="mt-6"><AdminContent /></TabsContent>
        <TabsContent value="fatture" className="mt-6"><AdminInvoices /></TabsContent>
        <TabsContent value="b2b" className="mt-6"><AdminB2B /></TabsContent>
        {user?.is_owner && <TabsContent value="staff" className="mt-6"><AdminStaff /></TabsContent>}
      </Tabs>
    </div>
  );
}
